import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import { 
  BarChart3, ChevronLeft, TrendingUp, TrendingDown, Minus,
  DollarSign, Target, Activity, AlertTriangle, RefreshCw
} from 'lucide-react';

const PlanActualComparison = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [projects, setProjects] = useState([]);
  const [request, setRequest] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    plan_version: '0',
    include_wbs_details: true,
    include_cost_element_details: false
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await psAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchComparison = async () => {
    if (!request.project_id) {
      toast.error('Please select a project');
      return;
    }

    try {
      setLoading(true);
      const response = await psAPI.getPlanActualComparison(request);
      setComparison(response.data);
    } catch (error) {
      console.error('Error fetching comparison:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate comparison');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-green-600';
    if (variance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (variance < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-gray-600" />;
  };

  const getUtilizationBadge = (percent) => {
    if (percent >= 100) return <Badge className="bg-red-100 text-red-800">Over Budget</Badge>;
    if (percent >= 80) return <Badge className="bg-amber-100 text-amber-800">Near Limit</Badge>;
    return <Badge className="bg-green-100 text-green-800">On Track</Badge>;
  };

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="plan-actual-comparison-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/ps/planning')}>
          <ChevronLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Plan vs Actual Comparison</h1>
          <p className="text-gray-500">Analyze project cost variances and budget utilization</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-48">
              <Label>Project *</Label>
              <Select 
                value={request.project_id || '__none__'} 
                onValueChange={(v) => setRequest(prev => ({ ...prev, project_id: v === '__none__' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Select project</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fiscal Year</Label>
              <Select 
                value={request.fiscal_year.toString()} 
                onValueChange={(v) => setRequest(prev => ({ ...prev, fiscal_year: parseInt(v) }))}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(year => (
                    <SelectItem key={year} value={year.toString()}>FY {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan Version</Label>
              <Select 
                value={request.plan_version} 
                onValueChange={(v) => setRequest(prev => ({ ...prev, plan_version: v }))}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Version 0</SelectItem>
                  <SelectItem value="1">Version 1</SelectItem>
                  <SelectItem value="2">Version 2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={request.include_wbs_details}
                onCheckedChange={(v) => setRequest(prev => ({ ...prev, include_wbs_details: v }))}
              />
              <Label className="text-sm">WBS Details</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={request.include_cost_element_details}
                onCheckedChange={(v) => setRequest(prev => ({ ...prev, include_cost_element_details: v }))}
              />
              <Label className="text-sm">Cost Element Details</Label>
            </div>
            <Button onClick={fetchComparison} disabled={loading}>
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <BarChart3 className="h-4 w-4 mr-2" />
              )}
              Generate Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {comparison && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Budget</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(comparison.total_budget)}</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-full">
                    <DollarSign className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Released: {formatCurrency(comparison.total_released_budget)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Planned Costs</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(comparison.total_planned_costs)}</p>
                  </div>
                  <div className="p-3 bg-orange-50 rounded-full">
                    <Target className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Actual Costs</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(comparison.total_actual_costs)}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-full">
                    <Activity className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Commitments: {formatCurrency(comparison.total_commitments)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Budget Utilization</p>
                    <p className={`text-2xl font-bold ${comparison.budget_utilization_percent >= 100 ? 'text-red-600' : comparison.budget_utilization_percent >= 80 ? 'text-amber-600' : 'text-green-600'}`}>
                      {formatPercent(comparison.budget_utilization_percent)}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${comparison.budget_utilization_percent >= 100 ? 'bg-red-50' : comparison.budget_utilization_percent >= 80 ? 'bg-amber-50' : 'bg-green-50'}`}>
                    {comparison.budget_utilization_percent >= 80 ? (
                      <AlertTriangle className={`h-6 w-6 ${comparison.budget_utilization_percent >= 100 ? 'text-red-600' : 'text-amber-600'}`} />
                    ) : (
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    )}
                  </div>
                </div>
                <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${comparison.budget_utilization_percent >= 100 ? 'bg-red-500' : comparison.budget_utilization_percent >= 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.min(comparison.budget_utilization_percent, 100)}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Variance Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Variance Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Cost Variance (Plan - Actual)</span>
                    {getVarianceIcon(comparison.total_cost_variance)}
                  </div>
                  <p className={`text-2xl font-bold ${getVarianceColor(comparison.total_cost_variance)}`}>
                    {formatCurrency(comparison.total_cost_variance)}
                  </p>
                  <p className={`text-sm ${getVarianceColor(comparison.total_cost_variance_percent)}`}>
                    {comparison.total_cost_variance >= 0 ? 'Under Plan' : 'Over Plan'} by {formatPercent(Math.abs(comparison.total_cost_variance_percent))}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Budget Remaining</span>
                    {getVarianceIcon(comparison.total_budget_remaining)}
                  </div>
                  <p className={`text-2xl font-bold ${getVarianceColor(comparison.total_budget_remaining)}`}>
                    {formatCurrency(comparison.total_budget_remaining)}
                  </p>
                  <p className="text-sm text-gray-500">
                    After Actuals + Commitments
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Status</span>
                  </div>
                  {getUtilizationBadge(comparison.budget_utilization_percent)}
                  <p className="text-sm text-gray-500 mt-2">
                    {comparison.total_budget_remaining >= 0 
                      ? `${formatCurrency(comparison.total_budget_remaining)} remaining`
                      : `${formatCurrency(Math.abs(comparison.total_budget_remaining))} over budget`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* WBS Details */}
          {comparison.wbs_details?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>WBS Element Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium text-gray-600">WBS Element</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Budget</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Planned</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Actual</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Commit.</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Variance</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Util. %</th>
                        <th className="pb-3 font-medium text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.wbs_details.map((wbs) => (
                        <tr key={wbs.wbs_element_id || wbs.wbs_element_name} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-3">
                            <span style={{ paddingLeft: `${(wbs.level - 1) * 16}px` }}>
                              {wbs.wbs_element_name}
                            </span>
                          </td>
                          <td className="py-3 text-right font-medium">{formatCurrency(wbs.budget)}</td>
                          <td className="py-3 text-right">{formatCurrency(wbs.planned_costs)}</td>
                          <td className="py-3 text-right">{formatCurrency(wbs.actual_costs)}</td>
                          <td className="py-3 text-right">{formatCurrency(wbs.commitments)}</td>
                          <td className={`py-3 text-right font-medium ${getVarianceColor(wbs.cost_variance)}`}>
                            <div className="flex items-center justify-end gap-1">
                              {getVarianceIcon(wbs.cost_variance)}
                              {formatCurrency(wbs.cost_variance)}
                            </div>
                          </td>
                          <td className="py-3 text-right">
                            <span className={
                              wbs.budget_utilization_percent >= 100 ? 'text-red-600 font-bold' :
                              wbs.budget_utilization_percent >= 80 ? 'text-amber-600' :
                              'text-green-600'
                            }>
                              {formatPercent(wbs.budget_utilization_percent)}
                            </span>
                          </td>
                          <td className="py-3">
                            {getUtilizationBadge(wbs.budget_utilization_percent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cost Element Details */}
          {comparison.cost_element_details?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Cost Element Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium text-gray-600">Cost Element</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Planned</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Actual</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Variance</th>
                        <th className="pb-3 font-medium text-gray-600 text-right">Variance %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.cost_element_details.map((ce) => (
                        <tr key={ce.cost_element_id || ce.cost_element_name} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="py-3">{ce.cost_element_name || ce.cost_element_id}</td>
                          <td className="py-3 text-right">{formatCurrency(ce.planned_amount)}</td>
                          <td className="py-3 text-right">{formatCurrency(ce.actual_amount)}</td>
                          <td className={`py-3 text-right font-medium ${getVarianceColor(ce.variance)}`}>
                            {formatCurrency(ce.variance)}
                          </td>
                          <td className={`py-3 text-right ${getVarianceColor(ce.variance_percent)}`}>
                            {formatPercent(ce.variance_percent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* No Results */}
      {!comparison && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>Select a project and click "Generate Report" to view the comparison.</p>
          </CardContent>
        </Card>
      )}
    </div>
    </Layout>
  );
};

export default PlanActualComparison;
